import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { useSelector } from "react-redux";

const Navigation = () => {
  const { language, t, toggleLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const settings = useSelector((state) => state.settings.data);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: t.nav.home },
    { to: "/why-us", label: t.nav.whyUs },
    { to: "/team", label: t.nav.team },
    { to: "/clients", label: t.nav.clients },
    { to: "/activities", label: t.nav.activities },
    { to: "/watch-price", label: "Watch Price" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div
          className={`nav-header transition-all duration-300 ${
            scrolled ? "shadow-md" : "shadow-sm"
          }`}
          style={{
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "0.5px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "9999px",
            padding: "0.5rem 1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "3.5rem",
          }}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            style={{
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "rgb(0, 55, 32)",
              textDecoration: "none",
            }}
          >
            {settings?.logo ? (
              <img
                src={settings.logo}
                alt="Logo"
                style={{
                  width: "36px",
                  height: "36px",
                  objectFit: "contain",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    "linear-gradient(to bottom right, #8FEC78, #81DD67)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                B
              </div>
            )}
            <span className="hidden sm:inline">{settings?.nama || "BMPS"}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="nav-link"
                style={{
                  color:
                    location.pathname === link.to
                      ? "rgb(0, 55, 32)"
                      : "rgb(131, 146, 140)",
                  background:
                    location.pathname === link.to
                      ? "rgba(0, 0, 0, 0.05)"
                      : "transparent",
                  textDecoration: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  padding: "6px 12px",
                  borderRadius: "9999px",
                  transition: "all 0.2s ease",
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              className="hidden sm:flex items-center gap-1 rounded-full"
              style={{
                color: "rgb(131, 146, 140)",
                padding: "6px 12px",
              }}
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-medium">
                {language === "en" ? "EN" : "ID"}
              </span>
            </Button>

            {/* Join Us Button (Desktop) */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ''}login`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex"
              style={{
                background:
                  "linear-gradient(to bottom right, #8FEC78, #81DD67)",
                color: "white",
                border: "none",
                borderRadius: "9999px",
                padding: "10px 20px",
                fontWeight: 600,
                fontSize: "0.875rem",
                textDecoration: "none",
              }}
            >
              {t.nav.joinUs}
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
              style={{
                background: "none",
                border: "none",
                color: "rgb(0, 55, 32)",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden absolute left-1/2 transform -translate-x-1/2 w-[90%] bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
          style={{
            top: "4.25rem",
            zIndex: 40,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "0.5px solid rgba(0,0,0,0.1)",
          }}
        >
          <div className="flex flex-col p-4 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="nav-link"
                style={{
                  color:
                    location.pathname === link.to
                      ? "rgb(0, 55, 32)"
                      : "rgb(131, 146, 140)",
                  background:
                    location.pathname === link.to
                      ? "rgba(0, 0, 0, 0.05)"
                      : "transparent",
                  textDecoration: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  padding: "6px 12px",
                  borderRadius: "9999px",
                  transition: "all 0.2s ease",
                }}
              >
                {link.label}
              </Link>
            ))}

            {/* Join Us Button Mobile */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || 'https://bmps.santani.dev'}/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center mt-2"
              style={{
                background: "linear-gradient(to bottom right, #8FEC78, #81DD67)",
                color: "white",
                border: "none",
                borderRadius: "9999px",
                padding: "10px 20px",
                fontWeight: 600,
                fontSize: "0.875rem",
                textDecoration: "none",
              }}
            >
              {t.nav.joinUs}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
